import FormLayout from '../models/FormLayout';

/** Redux-form prefix for a group's values (avoids collisions with field UUIDs). */
export function formGroupReduxPrefix(groupId) {
    return `grp_${groupId}`;
}

/**
 * Root-level layout rows: root fields and groups interleaved by `position`.
 *
 * @param {import('../models/FormLayoutField').default[]} fields
 * @param {import('../models/FormLayoutFieldGroup').default[]} groups
 * @returns {{ kind: 'field'|'group', ref: object }[]}
 */
export function getRootLayoutSequence(fields, groups) {
    const roots = (fields || []).filter((f) => !(f.groupId ?? f.group_id));
    const grps = groups || [];
    return [
        ...roots.map((f) => ({ kind: 'field', ref: f })),
        ...grps.map((g) => ({ kind: 'group', ref: g })),
    ].sort((a, b) => (a.ref.position || 0) - (b.ref.position || 0));
}

/**
 * @param {object[]} steps
 * @returns {object[]}
 */
export function getSortedSteps(steps) {
    return [...(steps || [])].sort((a, b) => (a.position || 0) - (b.position || 0));
}

/**
 * Root field/group effective step when wizard steps exist.
 *
 * @param {{ stepId?: string|null, step_id?: string|null }} ref
 * @param {object[]} sortedSteps from {@link getSortedSteps}
 * @returns {string|null}
 */
export function getEffectiveRootStepId(ref, sortedSteps) {
    if (!sortedSteps.length) {
        return null;
    }
    const firstId = sortedSteps[0]?.id ?? null;
    const raw = ref.stepId ?? ref.step_id ?? null;
    if (raw && sortedSteps.some((s) => s.id === raw)) {
        return raw;
    }
    return firstId;
}

/**
 * Root layout rows for one wizard step (same shape as {@link getRootLayoutSequence} items).
 *
 * @param {import('../models/FormLayoutField').default[]} fields
 * @param {import('../models/FormLayoutFieldGroup').default[]} groups
 * @param {object[]} steps
 * @param {string} stepId
 * @returns {{ kind: 'field'|'group', ref: object }[]}
 */
export function getStepLayoutSequence(fields, groups, steps, stepId) {
    const sortedSteps = getSortedSteps(steps);
    if (!sortedSteps.length || !stepId) {
        return [];
    }
    return getRootLayoutSequence(fields, groups)
        .filter((item) => getEffectiveRootStepId(item.ref, sortedSteps) === stepId)
        .sort((a, b) => (a.ref.position || 0) - (b.ref.position || 0));
}

/**
 * Root items with no explicit step id (for editor migration hints).
 *
 * @param {import('../models/FormLayoutField').default[]} fields
 * @param {import('../models/FormLayoutFieldGroup').default[]} groups
 * @param {object[]} steps
 */
export function getOrphanRootSequence(fields, groups, steps) {
    if (!(steps || []).length) {
        return [];
    }
    return getRootLayoutSequence(fields, groups).filter((item) => {
        const raw = item.ref.stepId ?? item.ref.step_id;
        return raw == null || raw === '';
    });
}

/**
 * @param {import('../models/FormLayoutField').default[]} fields
 * @param {string} groupId
 */
export function getSortedFieldsInGroup(fields, groupId) {
    return (fields || [])
        .filter((f) => (f.groupId ?? f.group_id) === groupId)
        .sort((a, b) => (a.position || 0) - (b.position || 0));
}

/**
 * Field array order for Kore `Form::set_fields`: the API overwrites each field's
 * `position` with 0..n-1 from **array order** (not from JSON `position`).
 * This order must match the visual root sequence: root rows interleaved with
 * groups, and group members contiguous in group field order.
 *
 * @param {object[]} fields
 * @param {object[]} groups
 * @returns {object[]}
 */
/**
 * Extra context paths for a tickettype_pricing field (nested scalars under the field name).
 * The form value is `{ tickettype_id, pricing_id }`; Engine resolves dotted paths via _.get.
 *
 * @param {object} field
 * @param {string} base Field path prefix (root name or group.field / group.0.field)
 * @returns {string[]}
 */
export function tickettypePricingWorkflowOutputSubpaths(field, base) {
    const type = field?.type || field?.getType?.();
    if (type !== FormLayout.TICKETTYPE_PRICING) {
        return [];
    }
    const prefix = String(base || '').trim();
    if (!prefix) {
        return [];
    }
    const content = field.content && typeof field.content === 'object' ? field.content : {};
    const paths = [`${prefix}.tickettype_id`];
    if (content.with_pricing !== false) {
        paths.push(`${prefix}.pricing_id`);
    }
    return paths;
}

/**
 * Dotted context paths for workflow transitions / runs. Root: `name`. Single group:
 * `groupName.fieldName`. Repeatable group: `groupName` (array of row maps), `groupName.0.fieldName`, `groupName_count`.
 * Mirrors Kore {@see Form::workflow_output_paths_for_form_id} for list `_value` / `_count` suffixes.
 *
 * @param {import('../models/FormLayout').default|object} formLayout
 * @returns {string[]}
 */
export function getFormLayoutWorkflowOutputPaths(formLayout) {
    const layout = formLayout instanceof FormLayout ? formLayout : new FormLayout(formLayout || { fields: [], groups: [] });
    const fields = layout.fields || [];
    const groups = layout.groups || [];
    const paths = [];

    const pushListExtras = (base, field) => {
        if (field.type !== FormLayout.LIST) {
            return;
        }
        paths.push(`${base}_value`);
        if (field.multiple) {
            paths.push(`${base}_count`);
        }
    };

    const pushFieldPaths = (base, field) => {
        paths.push(base);
        pushListExtras(base, field);
        tickettypePricingWorkflowOutputSubpaths(field, base).forEach((p) => paths.push(p));
    };

    fields.forEach((f) => {
        if (FormLayout.isDisplayOnlyField(f.type)) {
            return;
        }
        if (f.groupId ?? f.group_id) {
            return;
        }
        const name = String(f.name || '').trim();
        if (!name) {
            return;
        }
        pushFieldPaths(name, f);
    });

    const repeatableGroupNames = new Set();
    groups.forEach((g) => {
        const gName = String(g.name || '').trim();
        if (!gName) {
            return;
        }
        const groupMultiple = !!g.multiple;
        if (groupMultiple) {
            repeatableGroupNames.add(gName);
        }
        getSortedFieldsInGroup(fields, g.id).forEach((f) => {
            if (FormLayout.isDisplayOnlyField(f.type)) {
                return;
            }
            const fn = String(f.name || '').trim();
            if (!fn) {
                return;
            }
            const base = groupMultiple ? `${gName}.0.${fn}` : `${gName}.${fn}`;
            pushFieldPaths(base, f);
        });
    });
    repeatableGroupNames.forEach((nm) => {
        paths.push(nm);
        paths.push(`${nm}_count`);
    });

    return paths;
}

/**
 * Metadata for each workflow context path derived from a form layout (for grouped path pickers).
 *
 * @typedef {{ path: string, category: 'root'|'group_simple'|'repeatable_row'|'repeatable_array'|'repeatable_count'|'list_value'|'list_count', groupName?: string }} FormLayoutPathEntry
 */

/**
 * Same paths as {@link getFormLayoutWorkflowOutputPaths}, with categories for UI hints.
 *
 * @param {import('../models/FormLayout').default|object} formLayout
 * @returns {FormLayoutPathEntry[]}
 */
export function getFormLayoutWorkflowOutputPathEntries(formLayout) {
    const layout = formLayout instanceof FormLayout ? formLayout : new FormLayout(formLayout || { fields: [], groups: [] });
    const fields = layout.fields || [];
    const groups = layout.groups || [];
    /** @type {FormLayoutPathEntry[]} */
    const entries = [];

    const pushListExtras = (base, field) => {
        if (field.type !== FormLayout.LIST) {
            return;
        }
        entries.push({ path: `${base}_value`, category: 'list_value', groupName: undefined });
        if (field.multiple) {
            entries.push({ path: `${base}_count`, category: 'list_count', groupName: undefined });
        }
    };

    const pushFieldEntries = (base, field, category, groupName) => {
        entries.push({ path: base, category, groupName });
        pushListExtras(base, field);
        tickettypePricingWorkflowOutputSubpaths(field, base).forEach((p) => {
            entries.push({ path: p, category, groupName });
        });
    };

    fields.forEach((f) => {
        if (FormLayout.isDisplayOnlyField(f.type)) {
            return;
        }
        if (f.groupId ?? f.group_id) {
            return;
        }
        const name = String(f.name || '').trim();
        if (!name) {
            return;
        }
        pushFieldEntries(name, f, 'root', undefined);
    });

    const repeatableGroupNames = new Set();
    groups.forEach((g) => {
        const gName = String(g.name || '').trim();
        if (!gName) {
            return;
        }
        const groupMultiple = !!g.multiple;
        if (groupMultiple) {
            repeatableGroupNames.add(gName);
        }
        getSortedFieldsInGroup(fields, g.id).forEach((f) => {
            if (FormLayout.isDisplayOnlyField(f.type)) {
                return;
            }
            const fn = String(f.name || '').trim();
            if (!fn) {
                return;
            }
            const base = groupMultiple ? `${gName}.0.${fn}` : `${gName}.${fn}`;
            pushFieldEntries(
                base,
                f,
                groupMultiple ? 'repeatable_row' : 'group_simple',
                gName
            );
        });
    });
    repeatableGroupNames.forEach((nm) => {
        entries.push({ path: nm, category: 'repeatable_array', groupName: nm });
        entries.push({ path: `${nm}_count`, category: 'repeatable_count', groupName: nm });
    });

    return entries;
}

/**
 * Repeatable group with the given root name, if any.
 *
 * @param {import('../models/FormLayout').default|object} formLayout
 * @param {string} groupName first segment of foreach `items_path` (e.g. `child`)
 * @returns {import('../models/FormLayoutFieldGroup').default|object|null}
 */
export function getRepeatableGroupByRootName(formLayout, groupName) {
    const gName = String(groupName || '').trim();
    if (!gName) {
        return null;
    }
    const layout = formLayout instanceof FormLayout ? formLayout : new FormLayout(formLayout || { fields: [], groups: [] });
    return (
        (layout.groups || []).find((g) => String(g.name || '').trim() === gName && !!g.multiple) || null
    );
}

/**
 * Context paths for one row object under a prefix (e.g. foreach `item_key`: `item.firstname`).
 * Mirrors field paths under `groupName.0.*` in {@link getFormLayoutWorkflowOutputPaths}.
 *
 * @param {import('../models/FormLayout').default|object} formLayout
 * @param {string} groupName repeatable group root name (same as `items_path` when it is a single segment)
 * @param {string} pathPrefix usually foreach `item_key`
 * @returns {string[]}
 */
export function getRepeatableGroupRowPathsUnderPrefix(formLayout, groupName, pathPrefix) {
    const group = getRepeatableGroupByRootName(formLayout, groupName);
    const prefix = String(pathPrefix || '').trim();
    if (!group || !prefix) {
        return [];
    }
    const layout = formLayout instanceof FormLayout ? formLayout : new FormLayout(formLayout || { fields: [], groups: [] });
    const fields = layout.fields || [];
    const paths = [];

    const pushListExtras = (base, field) => {
        if (field.type !== FormLayout.LIST) {
            return;
        }
        paths.push(`${base}_value`);
        if (field.multiple) {
            paths.push(`${base}_count`);
        }
    };

    getSortedFieldsInGroup(fields, group.id).forEach((f) => {
        if (FormLayout.isDisplayOnlyField(f.type)) {
            return;
        }
        const fn = String(f.name || '').trim();
        if (!fn) {
            return;
        }
        const base = `${prefix}.${fn}`;
        paths.push(base);
        pushListExtras(base, f);
        tickettypePricingWorkflowOutputSubpaths(f, base).forEach((p) => paths.push(p));
    });

    return paths;
}

function appendSequenceRowsToFieldOrder(list, seq, byId, ordered, seen) {
    seq.forEach((item) => {
        if (item.kind === 'field') {
            const f = byId.get(item.ref.id);
            if (f && !seen.has(f.id)) {
                ordered.push(f);
                seen.add(f.id);
            }
        } else {
            getSortedFieldsInGroup(list, item.ref.id).forEach((ref) => {
                const f = byId.get(ref.id);
                if (f && !seen.has(f.id)) {
                    ordered.push(f);
                    seen.add(f.id);
                }
            });
        }
    });
}

export function getFieldsOrderedForKoreApi(fields, groups, steps) {
    const list = fields || [];
    const grps = groups || [];
    const st = steps || [];
    if (st.length === 0) {
        if (grps.length === 0) {
            return [...list].sort((a, b) => (a.position || 0) - (b.position || 0));
        }
        const byId = new Map(list.map((f) => [f.id, f]));
        const seq = getRootLayoutSequence(list, grps);
        const ordered = [];
        const seen = new Set();
        appendSequenceRowsToFieldOrder(list, seq, byId, ordered, seen);
        list.forEach((f) => {
            if (!seen.has(f.id)) {
                ordered.push(f);
            }
        });
        return ordered;
    }

    const sortedSteps = getSortedSteps(st);
    const byId = new Map(list.map((f) => [f.id, f]));
    const seq = getRootLayoutSequence(list, grps);
    const orderedRows = [];
    const seenRowIds = new Set();
    sortedSteps.forEach((step) => {
        seq
            .filter((item) => getEffectiveRootStepId(item.ref, sortedSteps) === step.id)
            .sort((a, b) => (a.ref.position || 0) - (b.ref.position || 0))
            .forEach((item) => {
                if (seenRowIds.has(item.ref.id)) {
                    return;
                }
                seenRowIds.add(item.ref.id);
                orderedRows.push(item);
            });
    });
    seq.forEach((item) => {
        if (seenRowIds.has(item.ref.id)) {
            return;
        }
        seenRowIds.add(item.ref.id);
        orderedRows.push(item);
    });

    const ordered = [];
    const seen = new Set();
    appendSequenceRowsToFieldOrder(list, orderedRows, byId, ordered, seen);
    list.forEach((f) => {
        if (!seen.has(f.id)) {
            ordered.push(f);
        }
    });
    return ordered;
}

/**
 * Whether every scalar in the row object is empty after trim.
 *
 * @param {Record<string, unknown>} row
 * @param {string[]} fieldIds
 */
function isRowEmpty(row, fieldIds) {
    if (!row || typeof row !== 'object') {
        return true;
    }
    return fieldIds.every((fid) => {
        const v = row[fid];
        if (v === undefined || v === null) {
            return true;
        }
        if (typeof v === 'string') {
            return v.trim() === '';
        }
        if (Array.isArray(v)) {
            return v.length === 0;
        }
        return false;
    });
}

/**
 * Turn redux-form values (field id keys + grp_* keys) into workflow-friendly payload
 * keyed by field/group `name`, nested objects/arrays for groups, plus `{name}_count` for multiples.
 *
 * @param {import('../models/FormLayout').default|object} formLayout
 * @param {Record<string, unknown>} rawValues
 * @returns {Record<string, unknown>}
 */
export function normalizeFormLayoutSubmitValues(formLayout, rawValues) {
    const layout = formLayout instanceof FormLayout ? formLayout : new FormLayout(formLayout);
    const fields = layout.fields || [];
    const groups = layout.groups || [];
    const out = {};
    const src = rawValues || {};

    const roots = fields.filter((f) => !(f.groupId ?? f.group_id) && !FormLayout.isDisplayOnlyField(f.type));
    roots.forEach((f) => {
        const name = String(f.name || '').trim();
        if (!name) {
            return;
        }
        if (Object.prototype.hasOwnProperty.call(src, f.id)) {
            out[name] = src[f.id];
            flattenTickettypePricingSubmit(out, f, src[f.id]);
        }
    });

    groups.forEach((group) => {
        const gName = String(group.name || '').trim();
        if (!gName) {
            return;
        }
        const prefix = formGroupReduxPrefix(group.id);
        const members = getSortedFieldsInGroup(fields, group.id)
            .filter((f) => !FormLayout.isDisplayOnlyField(f.type));
        const memberIds = members.map((m) => m.id);

        if (!group.multiple) {
            const row = src[prefix];
            const named = {};
            if (row && typeof row === 'object' && !Array.isArray(row)) {
                members.forEach((f) => {
                    const fn = String(f.name || '').trim();
                    if (!fn) {
                        return;
                    }
                    if (Object.prototype.hasOwnProperty.call(row, f.id)) {
                        named[fn] = row[f.id];
                        flattenTickettypePricingSubmit(named, f, row[f.id]);
                    }
                });
            }
            out[gName] = named;
            return;
        }

        const rows = Array.isArray(src[prefix]) ? src[prefix] : [];
        const cleaned = rows.filter((row) => !isRowEmpty(row, memberIds));
        const namedRows = cleaned.map((row) => {
            const named = {};
            if (!row || typeof row !== 'object') {
                return named;
            }
            members.forEach((f) => {
                const fn = String(f.name || '').trim();
                if (!fn) {
                    return;
                }
                if (Object.prototype.hasOwnProperty.call(row, f.id)) {
                    named[fn] = row[f.id];
                    flattenTickettypePricingSubmit(named, f, row[f.id]);
                }
            });
            return named;
        });
        out[gName] = namedRows;
        out[`${gName}_count`] = namedRows.length;
    });

    return out;
}

/**
 * Emit scalar tickettype_id / pricing_id keys alongside the object value.
 *
 * @param {Record<string, unknown>} target
 * @param {object} field
 * @param {unknown} value
 */
function flattenTickettypePricingSubmit(target, field, value) {
    const type = field?.type || field?.getType?.();
    if (type !== FormLayout.TICKETTYPE_PRICING) {
        return;
    }
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return;
    }
    const content = field.content && typeof field.content === 'object' ? field.content : {};
    const tickettypeName = String(content.tickettype_name || 'tickettype_id').trim() || 'tickettype_id';
    const pricingName = String(content.pricing_name || 'pricing_id').trim() || 'pricing_id';
    const withPricing = content.with_pricing !== false;

    if (value.tickettype_id != null && value.tickettype_id !== '') {
        target[tickettypeName] = value.tickettype_id;
    }
    if (withPricing && value.pricing_id != null && value.pricing_id !== '') {
        target[pricingName] = value.pricing_id;
    }
}

/**
 * Initial values for redux-form `formRenderer` when the layout has groups.
 *
 * @param {import('../models/FormLayout').default|object} formLayout
 * @param {object} [resources] Form resources (e.g. sectors) for default resolution
 * @returns {Record<string, unknown>}
 */
export function formRendererInitialValuesWithGroups(formLayout, resources) {
    const layout = formLayout instanceof FormLayout ? formLayout : new FormLayout(formLayout);
    const initial = {};
    const fields = layout.fields || [];
    const groups = layout.groups || [];

    fields.forEach((fld) => {
        if (fld.groupId ?? fld.group_id) {
            return;
        }
        if (typeof fld.getFormInitialValue !== 'function') {
            return;
        }
        const v = fld.getFormInitialValue(resources);
        if (v === undefined || v === '') {
            return;
        }
        if (Array.isArray(v) && v.length === 0) {
            return;
        }
        initial[fld.id] = v;
    });

    groups.forEach((group) => {
        const prefix = formGroupReduxPrefix(group.id);
        const members = getSortedFieldsInGroup(fields, group.id);
        const minItems = group.minItems ?? group.min_items;
        if (!group.multiple) {
            const row = {};
            members.forEach((f) => {
                if (typeof f.getFormInitialValue !== 'function') {
                    return;
                }
                const v = f.getFormInitialValue(resources);
                if (v !== undefined && v !== '') {
                    row[f.id] = v;
                }
            });
            if (Object.keys(row).length > 0) {
                initial[prefix] = row;
            }
            return;
        }
        const rowTemplate = {};
        members.forEach((f) => {
            if (typeof f.getFormInitialValue !== 'function') {
                return;
            }
            const v = f.getFormInitialValue(resources);
            if (v !== undefined && v !== '') {
                rowTemplate[f.id] = v;
            }
        });
        const min = Math.max(1, Number(minItems) || 1);
        const rows = [];
        for (let i = 0; i < min; i += 1) {
            rows.push({ ...rowTemplate });
        }
        initial[prefix] = rows;
    });

    return initial;
}

/**
 * Flat map field `name` -> value for submit validation (includes nested group names as objects/arrays).
 *
 * @param {import('../models/FormLayout').default|object} formLayout
 * @param {Record<string, unknown>} rawValues
 */
export function formValuesByFieldNameWithGroups(formLayout, rawValues) {
    return normalizeFormLayoutSubmitValues(formLayout, rawValues);
}
